/**
 * <s-compare> — laptop comparison tray.
 * Stores up to `data-max` picked products in localStorage and slides up from the
 * bottom of the page while anything is selected.
 *
 * Commands (on the element's id):
 *   --toggle — add/remove the product described by the source's data-handle/title/image/url
 *   --remove — remove the product named by the source's data-handle
 *   --clear  — empty the tray
 */
// Shared contract: s-compare-table.js reads and writes this same key.
const STORAGE_KEY = 'theme:compare';

function readItems() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((item) => item && item.handle) : [];
  } catch {
    return [];
  }
}

function writeItems(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Storage can be unavailable in private modes; the tray still works for this page view.
  }
}

class SCompare extends HTMLElement {
  #ac;
  #items = [];
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#items = readItems().slice(0, this.#max);

    this.addEventListener('command', (event) => this.#onCommand(event), opts);
    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY) return;
      this.#items = readItems().slice(0, this.#max);
      this.#render();
    }, opts);

    // Product grids refresh through partials (filters, pagination); resync new toggles.
    const main = document.getElementById('main-content');
    if (main) {
      this.#observer = new MutationObserver(() => this.#syncToggles());
      this.#observer.observe(main, { childList: true, subtree: true });
    }

    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  get #max() {
    return Number(this.dataset.max) || 3;
  }

  #onCommand(event) {
    const source = event.source;
    if (!(source instanceof HTMLElement)) return;

    if (event.command === '--toggle') {
      event.preventDefault();
      this.#toggle(source.dataset);
    } else if (event.command === '--remove') {
      event.preventDefault();
      const item = this.#items.find((entry) => entry.handle === source.dataset.handle);
      this.#items = this.#items.filter((entry) => entry.handle !== source.dataset.handle);
      if (item) this.#announce(this.dataset.removedLabel, item.title);
      this.#commit();
      if (this.#items.length === 0) document.activeElement?.blur?.();
    } else if (event.command === '--clear') {
      event.preventDefault();
      this.#items = [];
      this.#commit();
    }
  }

  #toggle({ handle, title = '', image = '', url = '' }) {
    if (!handle) return;
    const exists = this.#items.some((item) => item.handle === handle);

    if (exists) {
      this.#items = this.#items.filter((item) => item.handle !== handle);
      this.#announce(this.dataset.removedLabel, title);
    } else if (this.#items.length >= this.#max) {
      this.#status(this.dataset.limitLabel || '');
      this.#shake();
      return;
    } else {
      this.#items = [...this.#items, { handle, title, image, url }];
      this.#announce(this.dataset.addedLabel, title);
    }
    this.#commit();
  }

  #commit() {
    writeItems(this.#items);
    this.#render();
  }

  #announce(template, title) {
    this.#status((template || '').replace('%TITLE%', title || ''));
  }

  #status(message) {
    const status = this.querySelector('[data-compare-status]');
    if (status) status.textContent = message;
  }

  #shake() {
    const panel = this.querySelector('[data-compare-panel]');
    if (!panel || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    panel.animate(
      [{ transform: 'translateX(0)' }, { transform: 'translateX(-6px)' }, { transform: 'translateX(6px)' }, { transform: 'translateX(0)' }],
      { duration: 240, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
    );
  }

  #render() {
    const panel = this.querySelector('[data-compare-panel]');
    const list = this.querySelector('[data-compare-list]');
    const count = this.querySelector('[data-compare-count]');
    const link = this.querySelector('[data-compare-link]');
    const open = this.#items.length > 0;

    if (panel) {
      panel.hidden = !open;
    }

    if (count) {
      count.textContent = (this.dataset.countLabel || '').replace('%COUNT%', String(this.#items.length));
    }

    if (link) {
      const handles = this.#items.map((item) => encodeURIComponent(item.handle)).join(',');
      const base = this.dataset.compareUrl || link.getAttribute('href') || '';
      link.setAttribute('href', handles ? `${base}&handles=${handles}` : base);
      if (this.#items.length >= 2) {
        link.removeAttribute('aria-disabled');
      } else {
        link.setAttribute('aria-disabled', 'true');
      }
    }

    if (list) {
      list.replaceChildren(...Array.from({ length: this.#max }, (_, index) => this.#slot(this.#items[index])));
    }

    this.#syncToggles();
  }

  #slot(item) {
    const li = document.createElement('li');

    if (!item) {
      li.className = 'flex h-14 items-center justify-center rounded-sm border border-dashed border-border px-2 text-center font-mono text-[10px] uppercase tracking-[.14em] text-on-surface-secondary';
      li.textContent = this.dataset.emptyLabel || '';
      return li;
    }

    li.className = 'relative flex h-14 min-w-0 items-center gap-2 rounded-sm border border-border bg-surface-secondary pe-9 ps-1.5';

    if (item.image) {
      const img = document.createElement('img');
      img.src = item.image;
      img.alt = '';
      img.width = 44;
      img.height = 44;
      img.loading = 'lazy';
      img.className = 'size-11 shrink-0 rounded-sm bg-surface object-cover';
      li.append(img);
    }

    const title = document.createElement('a');
    title.href = item.url || '#';
    title.textContent = item.title;
    title.className = 'line-clamp-2 min-w-0 text-xs font-medium leading-tight text-on-surface hover:underline';
    li.append(title);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.setAttribute('commandfor', this.id);
    remove.setAttribute('command', '--remove');
    remove.dataset.handle = item.handle;
    remove.setAttribute('aria-label', (this.dataset.removeLabel || '').replace('%TITLE%', item.title));
    remove.className = 'absolute inset-y-0 end-0 inline-flex w-9 cursor-pointer items-center justify-center text-on-surface-secondary hover:text-on-surface focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent';
    remove.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';
    li.append(remove);

    return li;
  }

  #syncToggles() {
    const selected = new Set(this.#items.map((item) => item.handle));
    document.querySelectorAll(`[commandfor="${this.id}"][command="--toggle"]`).forEach((toggle) => {
      const pressed = selected.has(toggle.dataset.handle) ? 'true' : 'false';
      if (toggle.getAttribute('aria-pressed') !== pressed) toggle.setAttribute('aria-pressed', pressed);
    });
  }
}

if (!customElements.get('s-compare')) customElements.define('s-compare', SCompare);
